<template>
  <div class="asset-visit-form-data">
    <template v-if="variant === 'grid'">
      <key-value-list variant="grid" :size="size">
        <key-value
          v-if="showCompanyName && companyName"
          :label="companyTitle || translatedCompanyTitle"
          :value="companyName" />
        <key-value
          v-if="assetVisit.visitType"
          :label="t('yard.headers.visitType')"
          :value="getAssetVisitTypeDisplayName(assetVisit.visitType)" />
        <key-value :label="phoneTitle || translatedPhoneTitle" :value="assetVisit.phone || '--'" />

        <key-value v-for="data of assetVisitFormData" :key="data.id" :label="data.label">
          <template #value>
            <span
              v-if="isDocument(data)"
              v-html="
                getCustomFieldFormattedValue(data, {
                  [CustomFieldType.Document]: { generateLink: true }
                })
              "></span>
            <span
              v-else-if="isMultiDocument(data)"
              v-html="
                getCustomFieldFormattedValue(data, {
                  [CustomFieldType.MultiDocument]: { generateLink: true }
                })
              "></span>
            <span v-else>{{ getCustomFieldFormattedValue(data) ?? '--' }}</span>
          </template>
        </key-value>

        <key-value
          :label="
            t(
              'assetVisit.dialogs.checkinNotificationPanelDialog.headers.driverAppointmentIdentifier'
            )
          "
          :value="assetVisit.driverAppointmentIdentifier || '--'" />

        <key-value
          :label="t('global.basicTerms.notes', 2)"
          :value="assetVisit.driverNotes"
          v-if="assetVisit.driverNotes" />
      </key-value-list>
    </template>

    <template v-else>
      <key-value-list :is-divided="isDivided" variant="list-stacked" :size="size">
        <editable-key-value
          :label="companyTitle || translatedCompanyTitle"
          :can-edit="isAllowCompanyEdit"
          :is-editing="isEditingCompany"
          :edit-button-props="{
            'data-testid': 'asset-visit-edit-company-btn'
          }"
          :save-button-props="{
            disabled: !newCompany || (!newCompany.id && !newCompany.name),
            'data-testid': 'asset-visit-save-company-btn'
          }"
          @edit="isEditingCompany = true"
          @cancel="isEditingCompany = false"
          @save="saveNewCompany">
          <template #value>
            <span data-testid="asset-visit-form-company-value" v-if="!isEditingCompany">
              {{ companyName }}
            </span>
          </template>
          <template #editing-value>
            <yard-view-company-select
              v-model="newCompany"
              data-testid="asset-visit-form-company-select" />
          </template>
        </editable-key-value>

        <editable-key-value
          :label="t('yard.headers.visitType')"
          :can-edit="allowVisitTypeEdit"
          :is-editing="isEditingVisitType"
          :edit-button-props="{
            'data-testid': 'asset-visit-edit-visit-type-btn'
          }"
          :save-button-props="{
            disabled: !newVisitType,
            'data-testid': 'asset-visit-save-visit-type-btn'
          }"
          @edit="isEditingVisitType = true"
          @cancel="isEditingVisitType = false"
          @save="saveNewVisitType">
          <template #value>
            <span data-testid="asset-visit-form-visit-type-value" v-if="!isEditingVisitType">
              {{ getAssetVisitTypeDisplayName(assetVisit.visitType) }}
            </span>
          </template>
          <template #editing-value>
            <v-autocomplete
              v-model="newVisitType"
              :items="visitTypeOptions"
              data-testid="yard-asset-visit-type-select"
              :label="null"
              variant="outlined"
              density="compact"
              hide-details />
          </template>
        </editable-key-value>

        <editable-key-value
          :label="phoneTitle || translatedPhoneTitle"
          :can-edit="allowPhoneEdit"
          :is-editing="isEditingPhone"
          :edit-button-props="{
            'data-testid': 'drivers-chat-edit-phone-btn'
          }"
          :save-button-props="{
            'data-testid': 'drivers-chat-save-driver-phone-btn'
          }"
          @edit="isEditingPhone = true"
          @cancel="isEditingPhone = false"
          @save="saveNewAssetVisitPhone">
          <template #value>
            <span data-testid="asset-visit-form-drivers-phone-value">
              {{ assetVisit.phone || '--' }}
            </span>
          </template>
          <template #editing-value>
            <phone-number-field
              dense
              v-model="newAssetVisitPhone"
              :validator="$validator"
              outlined
              hide-details
              class="phone-number-field"
              data-testid="asset-visit-form-drivers-phone"
              :placeholder="
                t('assetVisit.dialogs.checkinNotificationPanelDialog.placeholders.driversPhone')
              "
              :label="null"></phone-number-field>
          </template>
        </editable-key-value>

        <key-value
          v-if="isSmartGateIntegrationEnabled"
          label="License Plate"
          :value="assetVisit.licensePlate || '--'" />
        <key-value
          v-if="isSmartGateIntegrationEnabled"
          label="DOT Number"
          :value="assetVisit.dotNumber || '--'" />

        <key-value v-for="data of assetVisitFormData" :key="data.id" :label="data.label">
          <template #value>
            <span
              v-if="isDocument(data)"
              v-html="
                getCustomFieldFormattedValue(data, {
                  [CustomFieldType.Document]: { generateLink: true }
                })
              "></span>
            <span
              v-else-if="isMultiDocument(data)"
              v-html="
                getCustomFieldFormattedValue(data, {
                  [CustomFieldType.MultiDocument]: { generateLink: true }
                })
              "></span>
            <span v-else>{{ getCustomFieldFormattedValue(data) ?? '--' }}</span>
          </template>
        </key-value>

        <key-value
          :label="
            t(
              'assetVisit.dialogs.checkinNotificationPanelDialog.headers.driverAppointmentIdentifier'
            )
          "
          :value="assetVisit.driverAppointmentIdentifier || '--'" />

        <key-value
          v-if="assetVisit.driverNotes"
          :label="t('global.basicTerms.notes')"
          :value="assetVisit.driverNotes" />
      </key-value-list>
    </template>
  </div>
</template>
<script>
import { computed, onMounted, ref, watch } from 'vue';
import {
  AssetVisitType,
  CustomFieldType,
  getCustomFieldFormattedValue,
  isFeatureEnabledForWarehouse,
  WarehouseFeatureEnum
} from '@nova/core';
import { useAssetVisit, useEventHub, useNotify } from '@/composables';
import { useI18n } from 'vue-i18n';
import { camelCase } from 'lodash';

import { KeyValueList, KeyValue, EditableKeyValue } from '@satellite/components/elements/key_value';
import customFormsService from '@satellite/services/custom-forms-service';
import YardViewCompanySelect from '@/modules/yard/components/Selects/YardViewCompanySelect.vue';
import PhoneNumberField from '@satellite/components/elements/forms/PhoneNumberField.vue';
import useYard from '@/modules/yard/composables/useYard';

export default {
  components: {
    KeyValueList,
    KeyValue,
    EditableKeyValue,
    YardViewCompanySelect,
    PhoneNumberField
  },
  emits: ['assetVisitTypeChanged'],
  props: {
    assetVisit: {
      type: Object,
      required: true
    },
    company: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    /**
     * TODO: ideally we should not need this prop; we could have a store
     * state that holds the selected warehouse (a global one, not a module-based)
     */
    warehouse: {
      type: Object,
      required: false,
      default: null
    },
    phoneTitle: {
      type: String,
      required: false
    },
    companyTitle: {
      type: String,
      required: false
    },
    variant: {
      type: String,
      required: false,
      default: 'list',
      validator(value) {
        return ['list', 'grid'].includes(value);
      }
    },
    size: {
      type: String,
      required: false,
      default: 'regular',
      validator(value) {
        return ['regular', 'small'].includes(value);
      }
    },
    isDivided: {
      type: Boolean,
      required: false,
      default: false
    },
    allowPhoneEdit: {
      type: Boolean,
      required: false,
      default: false
    },
    allowCompanyEdit: {
      type: Boolean,
      required: false,
      default: false
    },
    allowVisitTypeEdit: {
      type: Boolean,
      required: false,
      default: false
    },
    showCompanyName: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const assetVisitFormData = ref([]);
    const isEditingPhone = ref(false);
    const isEditingCompany = ref(false);
    const isEditingVisitType = ref(false);
    const newVisitType = ref(props.assetVisit.visitType || null);
    const isAllowCompanyEdit = computed(() => props.allowCompanyEdit && !!props.warehouse);
    const newCompany = ref(props.company ?? { name: props.assetVisit.companyHint });

    const { getAssetVisitTypeDisplayName } = useYard();

    const notify = useNotify();

    const newAssetVisitPhone = ref(null);
    const { updateAssetVisit } = useAssetVisit(props.assetVisit);

    const translatedPhoneTitle = computed(() =>
      t('assetVisit.dialogs.checkinNotificationPanelDialog.headers.phoneNumber')
    );
    const translatedCompanyTitle = computed(() =>
      t('assetVisit.dialogs.checkinNotificationPanelDialog.headers.carrierCompany')
    );

    const eventHub = useEventHub();

    const companyName = computed(() => {
      return props.company?.name ?? props.assetVisit.companyHint;
    });

    const isSmartGateIntegrationEnabled = computed(() => {
      return isFeatureEnabledForWarehouse(props.warehouse, WarehouseFeatureEnum.SmartGate);
    });

    async function getAssetVisitFormData() {
      try {
        const fields = await customFormsService.getCustomFormData({
          s: { objectId: props.assetVisit.id },
          limit: 1000
        });
        return fields.map(field => {
          field.formField.field = {
            ...field.formField.field,
            label: t(
              `core.customForms.customFormsFeaturesData.labels.${camelCase(field.label)}`,
              field.label
            )
          };
          return {
            ...field,
            label: t(
              `core.customForms.customFormsFeaturesData.labels.${camelCase(field.label)}`,
              field.label
            )
          };
        });
      } catch {
        return [];
      }
    }

    function isDocument(field) {
      return field.type === CustomFieldType.Document;
    }

    function isMultiDocument(field) {
      return field.type === CustomFieldType.MultiDocument;
    }

    const visitTypeOptions = Object.keys(AssetVisitType)
      .filter(t => t !== AssetVisitType.Unknown)
      .map(type => ({
        title: getAssetVisitTypeDisplayName(type),
        value: type
      }));

    const saveNewAssetVisitPhone = async () => {
      const updated = await updateAssetVisit(props.assetVisit.id, {
        phone: newAssetVisitPhone.value
      });
      isEditingPhone.value = false;
      eventHub.$emit('update-AssetVisit', updated || props.assetVisit);
    };

    const saveNewVisitType = async () => {
      if (!newVisitType.value) {
        notify(t('assetVisit.messages.visitTypeIsRequired'), 'warning');
        return;
      }

      const updated = await updateAssetVisit(props.assetVisit.id, {
        visitType: newVisitType.value
      });
      isEditingVisitType.value = false;
      eventHub.$emit('update-AssetVisit', updated || props.assetVisit);

      // Emit event to parent component for side-effects
      emit('assetVisitTypeChanged', newVisitType.value);
    };

    const saveNewCompany = async () => {
      const payload = {};

      if (!newCompany.value) {
        notify(t('assetVisit.messages.companyIsRequired'), 'warning');
        return;
      }

      if (newCompany.value.id) {
        payload.companyId = newCompany.value.id;
      } else if (newCompany.value.name) {
        payload.companyHint = newCompany.value.name;
      }

      const updated = await updateAssetVisit(props.assetVisit.id, payload);
      isEditingCompany.value = false;
      eventHub.$emit('update-AssetVisit', updated || props.assetVisit);
    };

    onMounted(async () => {
      assetVisitFormData.value = await getAssetVisitFormData();
    });

    watch(
      () => props.assetVisit.id,
      async () => {
        if (props.assetVisit.id) {
          assetVisitFormData.value = await getAssetVisitFormData();
        }
      }
    );

    return {
      assetVisitFormData,
      isDocument,
      isMultiDocument,
      isAllowCompanyEdit,
      isSmartGateIntegrationEnabled,
      newCompany,
      visitTypeOptions,
      isEditingCompany,
      CustomFieldType,
      saveNewCompany,
      isEditingVisitType,
      newVisitType,
      getCustomFieldFormattedValue,
      isEditingPhone,
      getAssetVisitTypeDisplayName,
      saveNewVisitType,
      saveNewAssetVisitPhone,
      newAssetVisitPhone,
      companyName,
      translatedPhoneTitle,
      translatedCompanyTitle,
      t
    };
  }
};
</script>

<style lang="scss">
.phone-number-field {
  .vue-tel-input {
    /* override the default margin-top applied in the phonenumber component */
    margin-top: 0 !important;
  }
}
</style>
